import React, { useState } from "react";
import { Select } from "antd";
import { CircleLoader } from "react-spinners";
import { LuUsers } from "react-icons/lu";
import useRequest from "../../components/hooks/use-request";
import { showToast } from "../../components/toast";
import {
  AudienceFilter,
  AudiencePreview,
  UserStatus,
  UserType,
  toMessage,
} from "./api";

const labelCls = "text-[13px] font-medium text-[#040821] mb-1.5 block";
const dateCls =
  "w-full h-[38px] px-3 border border-[#0979A1] rounded-lg text-[13px] outline-none focus:border-[#0979A1]";

// A date input needs yyyy-MM-dd; the API wants a full ISO timestamp.
const toISO = (v: string) => (v ? new Date(v).toISOString() : undefined);
const fromISO = (v?: string) => (v ? v.slice(0, 10) : "");

interface Props {
  value: AudienceFilter;
  onChange: (next: AudienceFilter) => void;
  disabled?: boolean;
  // When the campaign is saved, recipients can be counted against the API
  campaignId?: string;
}

const AudienceFilterPanel = ({
  value,
  onChange,
  disabled,
  campaignId,
}: Props) => {
  const [audience, setAudience] = useState<AudiencePreview | null>(null);

  const { makeRequest: previewAudience, loading: counting } = useRequest(
    `/email-campaigns/${campaignId}/preview-audience`,
    "POST"
  );

  // Only send keys that are actually set — a cleared control drops its key
  const patch = (next: Partial<AudienceFilter>) => {
    const merged: AudienceFilter = { ...value, ...next };
    (Object.keys(merged) as (keyof AudienceFilter)[]).forEach((k) => {
      const v = merged[k];
      if (
        v === undefined ||
        v === null ||
        v === "" ||
        (Array.isArray(v) && v.length === 0)
      )
        delete merged[k];
    });
    onChange(merged);
    setAudience(null); // the previous count no longer matches the filter
  };

  const handleCount = async () => {
    const [res, code] = await previewAudience(value);
    if (code >= 200 && code < 300 && res?.data) {
      setAudience(res.data);
    } else {
      showToast(toMessage(res, "Could not count recipients"), false, {
        position: "top-center",
      });
    }
  };

  return (
    <div className="email-mgt bg-white rounded-xl border border-gray-100 shadow-sm p-6">
      <h3 className="text-[16px] font-semibold mb-1">Audience</h3>
      <p className="text-[13px] text-gray-500 mb-5">
        Who receives this campaign. Leave a filter empty to not filter on it.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>User type</label>
          <Select
            mode="multiple"
            allowClear
            disabled={disabled}
            style={{ width: "100%" }}
            placeholder="All user types"
            value={value.userTypes || []}
            onChange={(v: UserType[]) => patch({ userTypes: v })}
            options={[
              { value: "buyer", label: "Buyers" },
              { value: "merchant", label: "Merchants" },
            ]}
          />
        </div>

        <div>
          <label className={labelCls}>Status</label>
          <Select
            mode="multiple"
            allowClear
            disabled={disabled}
            style={{ width: "100%" }}
            placeholder="All statuses"
            value={value.statuses || []}
            onChange={(v: UserStatus[]) => patch({ statuses: v })}
            options={[
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
            ]}
          />
        </div>

        <div>
          <label className={labelCls}>Email verified</label>
          <Select
            allowClear
            disabled={disabled}
            style={{ width: "100%" }}
            placeholder="Any"
            value={
              value.emailVerified === undefined
                ? undefined
                : String(value.emailVerified)
            }
            onChange={(v?: string) =>
              patch({ emailVerified: v === undefined ? undefined : v === "true" })
            }
            options={[
              { value: "true", label: "Verified only" },
              { value: "false", label: "Unverified only" },
            ]}
          />
        </div>

        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer h-[38px]">
            <input
              type="checkbox"
              disabled={disabled}
              checked={!!value.includeNewsletterSubscribers}
              onChange={(e) =>
                patch({ includeNewsletterSubscribers: e.target.checked || undefined })
              }
              className="w-4 h-4 accent-[#0979A1] cursor-pointer"
            />
            <span className="text-[13px] text-[#040821]">
              Include newsletter subscribers
            </span>
          </label>
        </div>

        <div>
          <label className={labelCls}>Signed up after</label>
          <input
            type="date"
            disabled={disabled}
            value={fromISO(value.createdAfter)}
            onChange={(e) => patch({ createdAfter: toISO(e.target.value) })}
            className={dateCls}
          />
        </div>

        <div>
          <label className={labelCls}>Signed up before</label>
          <input
            type="date"
            disabled={disabled}
            min={fromISO(value.createdAfter)}
            value={fromISO(value.createdBefore)}
            onChange={(e) => patch({ createdBefore: toISO(e.target.value) })}
            className={dateCls}
          />
        </div>
      </div>

      {/* Recipient count */}
      <div className="mt-5 border-t border-gray-100 pt-4">
        {campaignId ? (
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleCount}
              disabled={counting}
              className="flex items-center gap-2 px-4 py-2 border border-[#0979A1] text-[#0979A1] rounded-lg text-[13px] font-medium hover:bg-[#E8F7FA] disabled:opacity-60">
              {counting ? (
                <CircleLoader color="#0979A1" loading size={16} />
              ) : (
                <LuUsers size={15} />
              )}
              Check recipients
            </button>
            {audience && (
              <div className="text-[13px]">
                <span className="font-semibold text-[#040821]">
                  {(audience.count || 0).toLocaleString()}
                </span>{" "}
                <span className="text-gray-600">
                  recipient{audience.count === 1 ? "" : "s"} match this filter
                </span>
                {audience.sample?.length > 0 && (
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    e.g. {audience.sample.slice(0, 3).join(", ")}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-[12px] text-gray-400">
            Save the campaign to check how many recipients match.
          </p>
        )}
        <p className="text-[11px] text-gray-400 mt-2">
          Counts run against the saved filter — save your changes first for an
          accurate number. A campaign cannot be approved with an empty audience.
        </p>
      </div>
    </div>
  );
};

export default AudienceFilterPanel;
